"use client";

import { useMemo } from "react";
import {
  CopyButton,
  ErrorNotice,
  Notice,
  StatCard,
  StatGrid,
} from "@/components/tool-kit/primitives";
import { RESULT_CARD, WIDGET_H3 } from "@/components/tool-kit/styles";
import { DerivativeSteps } from "@/components/tools/calculus/derivative-steps";
import { pointValueText, shown } from "@/components/tools/calculus/format";
import { FunctionGraph } from "@/components/tools/calculus/function-graph";
import { MathView } from "@/components/tools/calculus/math-view";
import { readConstant } from "@/components/tools/calculus/read-constant";
import type { DERIVATIVE_CALCULATOR_STRINGS } from "@/components/tools/derivative-calculator";
import type { useToolNumber } from "@/components/tools/tool-ui";
import { t } from "@/lib/i18n/interpolate";
import { parseNumberInput } from "@/lib/parse-number";
import {
  containsAbs,
  type DerivativeReport,
} from "@/lib/tool-kit/calculus/derivative-report";
import { compile } from "@/lib/tool-kit/calculus/evaluate";
import { primeLatex, primeSymbol } from "@/lib/tool-kit/calculus/notation";
import { autoWindow } from "@/lib/tool-kit/calculus/plot";
import { toLatex, toText } from "@/lib/tool-kit/calculus/print";
import { valueAt } from "@/lib/tool-kit/calculus/value";
import { CHART_COLORS } from "@/lib/tool-kit/palette";

type Strings = typeof DERIVATIVE_CALCULATOR_STRINGS;
type Fmt = ReturnType<typeof useToolNumber>;

const DEFAULT_FROM = -5;
const DEFAULT_TO = 5;

function Answer({
  report,
  order,
  strings,
}: {
  report: DerivativeReport;
  order: number;
  strings: Strings;
}) {
  const { derivatives, variable, decimals } = report;
  const answer = derivatives[order - 1];
  const symbol = primeSymbol(order, "f", variable);
  const latex = `${primeLatex(order, "f", variable)} = ${toLatex(answer, { decimals })}`;
  return (
    <div className={`${RESULT_CARD} space-y-3`}>
      <h3 className={WIDGET_H3}>{strings.derivativeHeading}</h3>
      <MathView
        block
        label={`${symbol} = ${shown(answer, decimals)}`}
        latex={latex}
      />
      <div className="flex flex-wrap gap-2">
        <CopyButton
          copiedLabel={strings.copied}
          label={strings.copyAnswer}
          small
          text={toText(answer, { decimals })}
        />
        <CopyButton
          copiedLabel={strings.copied}
          label={strings.copyLatex}
          small
          text={toLatex(answer, { decimals })}
        />
      </div>
    </div>
  );
}

function Higher({
  report,
  strings,
}: {
  report: DerivativeReport;
  strings: Strings;
}) {
  const { derivatives, variable, decimals } = report;
  return (
    <div className="space-y-2">
      <h3 className={WIDGET_H3}>
        {t(strings.higherHeading, { order: derivatives.length })}
      </h3>
      <ul className="space-y-1 text-foreground text-sm">
        {derivatives.map((derivative, index) => (
          <li
            className="break-words font-mono"
            key={primeSymbol(index + 1, "f", variable)}
          >
            {primeSymbol(index + 1, "f", variable)} ={" "}
            {shown(derivative, decimals)}
          </li>
        ))}
      </ul>
    </div>
  );
}

function PointValues({
  report,
  order,
  pointText,
  strings,
  fmt,
}: {
  report: DerivativeReport;
  order: number;
  pointText: string;
  strings: Strings;
  fmt: Fmt;
}) {
  const point = readConstant(pointText);
  if (point === null) {
    return null;
  }
  if (point === "invalid") {
    return <ErrorNotice>{strings.pointInvalid}</ErrorNotice>;
  }
  const { ast, variable, derivatives, decimals } = report;
  const atFunction = valueAt(ast, variable, point.ast, point.value);
  const atDerivative = valueAt(
    derivatives[order - 1],
    variable,
    point.ast,
    point.value
  );
  return (
    <div className="space-y-2">
      <h3 className={WIDGET_H3}>
        {t(strings.valuesHeading, {
          variable,
          point: shown(point.ast, decimals),
        })}
      </h3>
      <StatGrid>
        <StatCard
          label={primeSymbol(0, "f", variable)}
          value={pointValueText(
            atFunction,
            fmt,
            strings.undefinedHere,
            decimals
          )}
        />
        <StatCard
          emphasis
          label={primeSymbol(order, "f", variable)}
          value={pointValueText(
            atDerivative,
            fmt,
            strings.undefinedHere,
            decimals
          )}
        />
      </StatGrid>
    </div>
  );
}

function GraphSection({
  report,
  order,
  pointText,
  fromText,
  toText: toValueText,
  strings,
  fmt,
}: {
  report: DerivativeReport;
  order: number;
  pointText: string;
  fromText: string;
  toText: string;
  strings: Strings;
  fmt: Fmt;
}) {
  const { ast, variable, variables, derivatives } = report;
  const answer = derivatives[order - 1];
  const from = parseNumberInput(fromText) ?? DEFAULT_FROM;
  const to = parseNumberInput(toValueText) ?? DEFAULT_TO;
  const [xMin, xMax] = from < to ? [from, to] : [DEFAULT_FROM, DEFAULT_TO];
  const f = useMemo(() => compile(ast, variable), [ast, variable]);
  const g = useMemo(() => compile(answer, variable), [answer, variable]);
  const window = useMemo(
    () => autoWindow([f, g], xMin, xMax),
    [f, g, xMin, xMax]
  );
  if (variables.length !== 1) {
    return variables.length > 1 ? <Notice>{strings.graphOther}</Notice> : null;
  }
  const point = readConstant(pointText);
  const marks =
    point && point !== "invalid"
      ? [f, g].map((curve, index) => ({
          x: point.value,
          y: curve(point.value),
          color: index === 0 ? CHART_COLORS[0] : CHART_COLORS[3],
          // The lower of the two points labels itself underneath, so labels never cross.
          below: curve(point.value) < (index === 0 ? g : f)(point.value),
          label: t(strings.pointLabelText, {
            x: fmt(point.value, { maximumFractionDigits: 3 }),
            y: fmt(curve(point.value), { maximumFractionDigits: 3 }),
          }),
        }))
      : [];
  const original = primeSymbol(0, "f", variable);
  const derived = primeSymbol(order, "f", variable);
  return (
    <div className="space-y-3">
      <h3 className={WIDGET_H3}>{strings.graphHeading}</h3>
      <FunctionGraph
        downloadPngLabel={strings.downloadPng}
        downloadSvgLabel={strings.downloadSvg}
        filename="derivative-graph"
        points={marks}
        series={[
          {
            f,
            color: CHART_COLORS[0],
            label: t(strings.seriesFunction, { name: original }),
          },
          {
            f: g,
            color: CHART_COLORS[3],
            label: t(strings.seriesDerivative, { name: derived }),
          },
        ]}
        title={t(strings.graphTitle, { original, derived })}
        window={window}
      />
    </div>
  );
}

export function DerivativeResults({
  report,
  order: requestedOrder,
  pointText,
  fromText,
  toText: toValueText,
  strings,
  fmt,
}: {
  report: DerivativeReport;
  order: number;
  pointText: string;
  fromText: string;
  toText: string;
  strings: Strings;
  fmt: Fmt;
}) {
  const { ast, variable, variables, decimals, derivatives, steps } = report;
  const order = Math.min(requestedOrder, derivatives.length);
  const others = variables.filter((name) => name !== variable);
  return (
    <>
      <p className="text-foreground text-sm">
        <span className="text-muted-foreground">{strings.readAs}: </span>
        <MathView
          label={shown(ast, decimals)}
          latex={`f(${variable}) = ${toLatex(ast, { decimals })}`}
        />
      </p>
      {variables.length === 0 ? <Notice>{strings.noVariable}</Notice> : null}
      <Answer order={order} report={report} strings={strings} />
      {report.truncated ? (
        <Notice>{t(strings.truncated, { order: derivatives.length })}</Notice>
      ) : null}
      {others.length > 0 ? (
        <Notice>{t(strings.otherLetters, { variable })}</Notice>
      ) : null}
      {containsAbs(ast) ? <Notice>{strings.absNote}</Notice> : null}
      {derivatives.length > 1 ? (
        <Higher report={report} strings={strings} />
      ) : null}
      <PointValues
        fmt={fmt}
        order={order}
        pointText={pointText}
        report={report}
        strings={strings}
      />
      <details className="space-y-3" open>
        <summary className="cursor-pointer font-semibold text-foreground text-lg">
          {strings.stepsHeading}
        </summary>
        <p className="mt-3 text-muted-foreground text-sm">
          {strings.stepsIntro}
        </p>
        <div className="mt-3">
          <DerivativeSteps
            decimals={decimals}
            steps={steps}
            strings={strings}
            variable={variable}
          />
        </div>
      </details>
      <GraphSection
        fmt={fmt}
        fromText={fromText}
        order={order}
        pointText={pointText}
        report={report}
        strings={strings}
        toText={toValueText}
      />
    </>
  );
}
